import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * A controlled single-input prompt that works inside the preview iframe
 * (replacement for the blocked `window.prompt`).
 *
 * Props:
 *   open, onOpenChange
 *   title, description
 *   initial          – initial value
 *   placeholder
 *   submitLabel      – button text (default "Save")
 *   onSubmit(value)  – may return a promise; dialog stays open while pending
 *   testid           – test id prefix (defaults to "text-prompt")
 */
export default function TextPromptDialog({
  open,
  onOpenChange,
  title,
  description,
  initial = "",
  placeholder,
  submitLabel = "Save",
  onSubmit,
  testid = "text-prompt",
}) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(initial || "");
      setBusy(false);
    }
  }, [open, initial]);

  async function submit(e) {
    e.preventDefault();
    const v = (value || "").trim();
    if (!v) return;
    setBusy(true);
    try {
      const result = onSubmit?.(v);
      if (result && typeof result.then === "function") await result;
      onOpenChange?.(false);
    } catch (err) {
      // caller is expected to toast
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange?.(o)}>
      <DialogContent data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-stone-500">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            data-testid={`${testid}-input`}
            autoFocus
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={busy}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !value.trim()}
              data-testid={`${testid}-submit`}
              className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
